/**
 * Destination identifiers for hotel search (city, hotel, or Google/TBO location).
 */

import {
  HOTEL_OUTSIDE_INVENTORY_PAGE_MESSAGE,
  HOTEL_OUTSIDE_INVENTORY_PAGE_MESSAGE_WITH_NAME,
} from '../constants/hotelSearch.constants';

export function hasValidSearchCoordinates(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat)
    && Number.isFinite(lng)
    && lat !== 0
    && lng !== 0
    && lat >= -90
    && lat <= 90
    && lng >= -180
    && lng <= 180;
}

export function hasHotelSearchDestination({
  cityId,
  hotelCode,
  locationId,
  latitude,
  longitude,
} = {}) {
  return Boolean(
    cityId
    || hotelCode
    || locationId
    || hasValidSearchCoordinates(latitude, longitude)
  );
}

export function isTboNumericCityId(value) {
  const raw = String(value ?? '').trim();
  if (!/^\d+$/.test(raw)) return false;
  return Number.parseInt(raw, 10) > 0;
}

export function shouldSearchByCityCode({ cityId, locationId } = {}) {
  if (locationId) return false;
  return isTboNumericCityId(cityId);
}

export function hotelSearchDestinationLabel({
  searchLocationName,
  hotelName,
  cityName,
} = {}) {
  return String(searchLocationName || hotelName || cityName || '').trim();
}

export function pickLocationId(item) {
  if (!item || typeof item !== 'object') return '';
  const direct = item.locationId || item.sourceId || item.placeId;
  if (direct) return String(direct).trim();
  const isLocation = item.type === 'location' || Boolean(item.locationType);
  if (isLocation && item.id) return String(item.id).trim();
  return '';
}

export function appendHotelGeoSearchParams(params, { latitude, longitude } = {}) {
  if (!hasValidSearchCoordinates(latitude, longitude)) return params;
  params.set('lat', String(latitude));
  params.set('lng', String(longitude));
  return params;
}

export function readHotelGeoSearchParams(searchParams) {
  if (!searchParams || typeof searchParams.get !== 'function') return {};
  const latitude = Number(searchParams.get('lat'));
  const longitude = Number(searchParams.get('lng'));
  if (!hasValidSearchCoordinates(latitude, longitude)) return {};
  return { latitude, longitude };
}

export function applyHotelGeoToSearchBody(body, { latitude, longitude } = {}) {
  if (!hasValidSearchCoordinates(latitude, longitude)) return body;
  body.latitude = Number(latitude);
  body.longitude = Number(longitude);
  return body;
}

export function hotelOutsideInventoryPageMessage(hotelName, {
  withName = HOTEL_OUTSIDE_INVENTORY_PAGE_MESSAGE_WITH_NAME,
  withoutName = HOTEL_OUTSIDE_INVENTORY_PAGE_MESSAGE,
} = {}) {
  const name = String(hotelName || '').trim();
  if (!name) return withoutName;
  return String(withName || '').replaceAll('{hotelName}', name);
}

export function isHotelOutsideInventoryNotice(searchContext) {
  if (!searchContext) return false;
  if (searchContext.inventoryAvailable === false) return true;
  const page = String(searchContext.pageMessage || '');
  const result = String(searchContext.resultMessage || '');
  return page === 'Hotel not available. Check nearby hotels'
    || result.includes('Find nearby to this')
    || result.includes("isn't currently listed on YovoTrip")
    || page.includes("isn't currently listed on YovoTrip");
}
